import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { track, ACTIVATION_EVENTS } from '../services/activation';

/*
  Kindra Health · Public homepage · V13 (2026-02)
  PREVIEW ONLY. Do not deploy to production until founder approves.

  V13 = MESSAGE POWER PASS (content completion + render fix).
  - Hero copy (left) kept: "Healthcare treats moments. Kindra understands
    the whole journey."
  - 5 rotating chat examples replaced with V13 copy (exhaustion,
    after-specialist, carer, blood results, new doctor).
  - Section order fixed: Hero → Why people need this → What makes Kindra
    different → Outcomes (newly rendered) → Appointment Brief → Health
    Memory → Trust → Final CTA → Footer.
  - Trust section rewritten: "Built for trust." + "Health information
    deserves extra care." + 4 pillars.
  - Final CTA rewritten: "See what healthcare feels like when someone
    finally remembers the whole picture."
*/

// ─── Brand tokens ─────────────────────────────────────────
const PAPER       = '#FBFAF6';
const NAVY        = '#0E1C2E';
const NAVY_MUTED  = 'rgba(14, 28, 46, 0.72)';
const NAVY_SOFT   = 'rgba(14, 28, 46, 0.55)';
const TEAL        = '#2FB3A0';
const TEAL_SOFT   = 'rgba(47, 179, 160, 0.10)';
const HAIRLINE    = 'rgba(14, 28, 46, 0.10)';
const SAGE_BG     = '#F4F0E6';

// ─── Glyphs ───────────────────────────────────────────────
const ArrowRight = ({ size = 16, stroke = 1.7 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CheckTick = ({ size = 14, color = TEAL }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M4 12l5 5L20 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── Top nav ──────────────────────────────────────────────
const TopNav = ({ onStart, onLogin, user }) => (
  <nav
    className="w-full sticky top-0 z-40 safe-top"
    style={{ backgroundColor: PAPER, borderBottom: `1px solid ${HAIRLINE}` }}
    data-testid="landing-nav"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2" data-testid="landing-nav-logo">
        <img src="/kindra-logo.png" alt="Kindra" className="h-9 md:h-10 w-auto" />
      </Link>
      <div className="flex items-center gap-1 md:gap-5">
        {user ? (
          <button onClick={() => (window.location.href = '/home')} className="text-[13px] md:text-[14px] font-medium px-3 md:px-4 py-2 rounded-full transition-colors" style={{ color: NAVY }} data-testid="landing-nav-continue">
            Continue to Kindra
          </button>
        ) : (
          <>
            <button onClick={onLogin} className="text-[13px] md:text-[14px] font-medium px-3 md:px-4 py-2 transition-opacity hover:opacity-70" style={{ color: NAVY }} data-testid="landing-nav-signin">
              Sign in
            </button>
            <button onClick={onStart} className="inline-flex items-center gap-1.5 rounded-full text-[13px] md:text-[14px] font-medium px-4 md:px-5 py-2 transition-colors" style={{ backgroundColor: NAVY, color: PAPER }} data-testid="landing-nav-cta">
              Try free
            </button>
          </>
        )}
      </div>
    </div>
  </nav>
);

// ─── Live chat demo data · V13 · the whole journey ────────
// Each reply demonstrates Kindra holding the whole picture — not just
// answering today's question but carrying history, organising, and
// producing a concrete artefact the person can use. Rotate elegantly.
const EXAMPLES = [
  {
    id: 'exhaustion',
    topic: 'Still exhausted',
    user: 'Why am I still exhausted?',
    pills: ['Ferritin · March · low', 'Fatigue · worsening', 'GP · Friday'],
    kindra:
      "Your ferritin was low in March, fatigue has worsened, and your GP visit is Friday. I've added 3 questions to ask and your recent results to your appointment brief.",
    action: { icon: '📋', text: '3 questions + recent results added to your Friday brief' },
  },
  {
    id: 'specialist-recap',
    topic: 'After the specialist',
    user: 'I forgot what the specialist said.',
    pills: ['Specialist visit · last week', 'Key points', 'Follow-up timing'],
    kindra:
      "I saved the key points, next steps and follow-up timing to your record so it's easy to revisit later.",
    action: { icon: '📝', text: 'Specialist summary saved to your record' },
  },
  {
    id: 'carer',
    topic: 'Caring for Mum',
    user: 'Mum has too many medications to track.',
    pills: ["Mum's care", 'Medication list', 'Recent changes'],
    kindra:
      "I've organised her medication list, recent changes and upcoming appointments into one place.",
    action: { icon: '📄', text: "Mum's medications + appointments in one place" },
  },
  {
    id: 'bloods',
    topic: 'Blood results',
    user: 'My blood results scared me.',
    pills: ['Recent pathology', 'Plain-English', 'For your doctor'],
    kindra:
      "I've explained what stands out, what is routine, and what is worth discussing with your doctor.",
    action: { icon: '💬', text: 'Results explained · ready to raise with your GP' },
  },
  {
    id: 'new-doctor',
    topic: 'New doctor tomorrow',
    user: 'New doctor tomorrow. Where do I start?',
    pills: ['New doctor · tomorrow', 'History + medications', 'Current concerns'],
    kindra:
      "I prepared a one-page summary of your history, medications and current concerns for the appointment.",
    action: { icon: '📋', text: 'One-page summary ready for tomorrow' },
  },
];

// ─── 2 · HERO · live chat demo ────────────────────────────
const Hero = ({ onStart, onSeeExamples }) => {
  const [active, setActive] = useState(0);
  const [fade, setFade] = useState(true);

  // Auto-rotate every 7 seconds with a quick fade transition.
  useEffect(() => {
    const tick = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setActive((a) => (a + 1) % EXAMPLES.length);
        setFade(true);
      }, 220);
    }, 7000);
    return () => clearInterval(tick);
  }, []);

  const e = EXAMPLES[active];

  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: PAPER }}
      data-testid="landing-hero"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-14 md:pt-20 lg:pt-24 pb-20 md:pb-24 lg:pb-28">
        <div className="lg:grid lg:grid-cols-12 lg:gap-14 lg:items-center">
          {/* LEFT · copy */}
          <div className="lg:col-span-5">
            <p
              className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-6"
              style={{ color: TEAL, letterSpacing: '0.24em' }}
              data-testid="hero-eyebrow"
            >
              Care between appointments
            </p>
            <h1
              className="font-serif text-[42px] sm:text-[56px] lg:text-[64px] xl:text-[72px] leading-[1.03] tracking-[-0.018em] font-normal"
              style={{ color: NAVY }}
              data-testid="hero-headline"
            >
              Healthcare treats moments.<br /><em className="italic">Kindra understands the whole journey.</em>
            </h1>
            <p
              className="font-sans mt-7 md:mt-8 text-[16px] md:text-[18.5px] leading-[1.55] max-w-[560px]"
              style={{ color: NAVY_MUTED }}
              data-testid="hero-subhead"
            >
              Your reports, medications, symptoms, appointments and history — held together in one intelligent companion that helps you make better <em className="italic">next moves</em>.
            </p>
            <div className="mt-9 md:mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={onStart}
                className="group inline-flex items-center justify-center gap-2 rounded-full px-8 md:px-9 py-4 md:py-[20px] text-[15px] md:text-[16px] font-medium font-sans transition-all hover:translate-y-[-1px]"
                style={{ backgroundColor: NAVY, color: PAPER, boxShadow: '0 12px 32px -14px rgba(14, 28, 46, 0.35)' }}
                data-testid="hero-cta-start-trial"
              >
                Start free
                <span className="transition-transform group-hover:translate-x-0.5">
                  <ArrowRight size={16} stroke={2} />
                </span>
              </button>
              <button
                onClick={onSeeExamples}
                className="group inline-flex items-center gap-1.5 py-3 text-[14px] md:text-[14.5px] font-medium font-sans transition-opacity hover:opacity-70"
                style={{ color: NAVY, borderBottom: `1px solid ${NAVY}` }}
                data-testid="hero-cta-watch-demo"
              >
                See it help someone like me
                <ArrowRight size={13} stroke={2} />
              </button>
            </div>
            <p
              className="font-sans mt-6 text-[12.5px] md:text-[13px]"
              style={{ color: NAVY_SOFT }}
              data-testid="hero-microline"
            >
              30 days free · No card needed · Private by design
            </p>
          </div>

          {/* RIGHT · live chat demo (the magic) */}
          <div className="mt-12 lg:mt-0 lg:col-span-7" data-testid="hero-chat">
            <div
              className="relative max-w-[620px] mx-auto lg:mx-0 lg:ml-auto rounded-3xl overflow-hidden"
              style={{
                backgroundColor: '#FFFFFF',
                border: `1px solid ${HAIRLINE}`,
                boxShadow: '0 32px 64px -28px rgba(14, 28, 46, 0.28)',
              }}
            >
              {/* Chat window header bar */}
              <div
                className="flex items-center gap-3 px-5 md:px-6 py-3.5 md:py-4"
                style={{ backgroundColor: PAPER, borderBottom: `1px solid ${HAIRLINE}` }}
                data-testid="hero-chat-header"
              >
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[15px]"
                  style={{ backgroundColor: TEAL_SOFT }}
                  aria-hidden
                >
                  🌿
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-[13px] font-medium" style={{ color: NAVY }}>
                    Kindra
                  </p>
                  <p
                    className="font-sans text-[10.5px] uppercase truncate"
                    style={{ color: TEAL, letterSpacing: '0.18em' }}
                    data-testid="hero-chat-topic"
                  >
                    {e.topic}
                  </p>
                </div>
                <span
                  className="inline-flex items-center gap-1.5 font-sans text-[11px]"
                  style={{ color: NAVY_SOFT }}
                  aria-hidden
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: TEAL }} />
                  live
                </span>
              </div>

              {/* Conversation area · smooth fade between examples */}
              <div
                className="px-5 md:px-7 py-6 md:py-7 transition-opacity duration-200"
                style={{ opacity: fade ? 1 : 0 }}
                data-testid="hero-chat-body"
              >
                {/* User bubble · right-aligned */}
                <div className="flex justify-end mb-4">
                  <div
                    className="max-w-[82%] rounded-2xl rounded-tr-sm px-4 py-3"
                    style={{ backgroundColor: NAVY, color: PAPER }}
                    data-testid="hero-chat-user"
                  >
                    <p className="font-sans text-[14.5px] md:text-[15px] leading-[1.4]">
                      {e.user}
                    </p>
                  </div>
                </div>

                {/* Using-your-context micro-row */}
                <div className="mb-3 flex items-start gap-2" data-testid="hero-chat-context">
                  <span
                    className="font-sans text-[10px] uppercase font-medium flex-shrink-0 mt-1"
                    style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}
                  >
                    Using your
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {e.pills.map((pill, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center rounded-full px-2.5 py-1 font-sans text-[11px]"
                        style={{ backgroundColor: TEAL_SOFT, color: TEAL }}
                        data-testid={`hero-chat-pill-${i}`}
                      >
                        {pill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Kindra bubble · left-aligned */}
                <div className="flex justify-start mb-4">
                  <div
                    className="max-w-[90%] rounded-2xl rounded-tl-sm px-5 py-4"
                    style={{ backgroundColor: PAPER, border: `1px solid ${HAIRLINE}` }}
                    data-testid="hero-chat-kindra"
                  >
                    <p
                      className="font-sans text-[14.5px] md:text-[15.5px] leading-[1.55]"
                      style={{ color: NAVY }}
                    >
                      {e.kindra}
                    </p>
                  </div>
                </div>

                {/* Action footer — what Kindra actually did */}
                <div
                  className="mt-4 rounded-2xl px-4 py-3 flex items-center gap-3"
                  style={{ backgroundColor: '#F0F9F6', border: `1px solid ${TEAL_SOFT}` }}
                  data-testid="hero-chat-action"
                >
                  <span
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full text-[16px]"
                    style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
                    aria-hidden
                  >
                    {e.action.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-sans text-[10px] uppercase font-medium mb-0.5"
                      style={{ color: TEAL, letterSpacing: '0.18em' }}
                    >
                      Kindra just did this for you
                    </p>
                    <p className="font-sans text-[13.5px] md:text-[14px]" style={{ color: NAVY }}>
                      {e.action.text}
                    </p>
                  </div>
                  <CheckTick size={15} />
                </div>
              </div>

              {/* Example picker / rotation indicators */}
              <div
                className="flex items-center justify-between px-5 md:px-6 py-3.5"
                style={{ borderTop: `1px solid ${HAIRLINE}`, backgroundColor: PAPER }}
                data-testid="hero-chat-dots"
              >
                <p className="font-sans text-[10.5px]" style={{ color: NAVY_SOFT, letterSpacing: '0.06em' }}>
                  Live examples · cycling
                </p>
                <div className="flex items-center gap-1.5">
                  {EXAMPLES.map((ex, i) => (
                    <button
                      key={ex.id}
                      onClick={() => { setFade(false); setTimeout(() => { setActive(i); setFade(true); }, 220); }}
                      className="block w-6 h-1 rounded-full transition-colors"
                      style={{ backgroundColor: i === active ? TEAL : HAIRLINE }}
                      aria-label={`Show example ${i + 1}: ${ex.topic}`}
                      data-testid={`hero-chat-dot-${i}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <p
              className="mt-5 md:mt-6 text-center font-sans text-[12.5px]"
              style={{ color: NAVY_SOFT }}
              data-testid="hero-chat-caption"
            >
              Real examples of Kindra using your context. Illustrative only — Kindra never prescribes or diagnoses.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── 2.5 · TRUST BELT · Stripe-style confidence line ──────
// Thin, calm, centred. No testimonials. No invented numbers. Just
// three honest commitments that sit under the hero as a confidence
// bridge before the below-fold argumentation begins.
const TrustBelt = () => (
  <section
    className="relative"
    style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}`, borderBottom: `1px solid ${HAIRLINE}` }}
    data-testid="landing-trust-belt"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-7">
      <div
        className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-sans text-[12.5px] md:text-[13.5px]"
        style={{ color: NAVY_MUTED }}
      >
        <span className="inline-flex items-center gap-2" data-testid="trust-belt-0">
          <CheckTick size={13} /> Never prescribes or diagnoses
        </span>
        <span className="hidden md:inline opacity-30" aria-hidden>·</span>
        <span className="inline-flex items-center gap-2" data-testid="trust-belt-1">
          <CheckTick size={13} /> Private by design — never sold
        </span>
        <span className="hidden md:inline opacity-30" aria-hidden>·</span>
        <span className="inline-flex items-center gap-2" data-testid="trust-belt-2">
          <CheckTick size={13} /> Built in Brisbane, Australia
        </span>
      </div>
    </div>
  </section>
);


// ─── Section "What makes Kindra different" · 3-card compare ─
const WhyGenericFails = () => {
  const cards = [
    { tag: 'Google',  icon: '🔍', line: 'Answers the question.', tone: 'sage' },
    { tag: 'Chatbot', icon: '💬', line: 'Answers what you typed today.', tone: 'sage' },
    { tag: 'Kindra',  icon: '🌿', line: 'Answers in light of your history, reports, medications, symptoms and upcoming care.', tone: 'navy' },
  ];
  return (
    <section
      className="relative"
      style={{ backgroundColor: '#FFFFFF', borderTop: `1px solid ${HAIRLINE}` }}
      data-testid="landing-why"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="max-w-[820px] mb-14 md:mb-20">
          <p
            className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-5"
            style={{ color: TEAL, letterSpacing: '0.24em' }}
          >
            What makes Kindra different
          </p>
          <h2
            className="font-serif text-[36px] md:text-[52px] lg:text-[60px] leading-[1.05] tracking-[-0.015em] font-normal"
            style={{ color: NAVY }}
            data-testid="why-headline"
          >
            Not generic answers. <em className="italic">Context-aware support.</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {cards.map((c, i) => {
            const isKindra = c.tone === 'navy';
            return (
              <article
                key={i}
                className="rounded-2xl p-7 md:p-9 flex flex-col"
                style={{
                  backgroundColor: isKindra ? NAVY : SAGE_BG,
                  color: isKindra ? PAPER : NAVY,
                  border: isKindra ? 'none' : `1px solid ${HAIRLINE}`,
                  minHeight: '240px',
                }}
                data-testid={`why-card-${i}`}
              >
                <div className="flex items-center gap-2.5 mb-6">
                  <span
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full text-[16px]"
                    style={{
                      backgroundColor: isKindra ? TEAL_SOFT : '#FFFFFF',
                      border: isKindra ? 'none' : `1px solid ${HAIRLINE}`,
                    }}
                    aria-hidden
                  >
                    {c.icon}
                  </span>
                  <p
                    className="font-sans text-[10.5px] uppercase font-medium"
                    style={{ color: isKindra ? TEAL : NAVY_SOFT, letterSpacing: '0.2em' }}
                    data-testid={`why-card-${i}-tag`}
                  >
                    {c.tag}
                  </p>
                </div>
                <p
                  className="font-serif text-[20px] md:text-[22px] leading-[1.4] tracking-[-0.005em]"
                  style={{ color: isKindra ? PAPER : NAVY_SOFT }}
                  data-testid={`why-card-${i}-line`}
                >
                  {c.line}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─── Section 2 · Real life doesn't happen in appointments ─
// 4 pain+relief cards. Pain as an italic quote, relief as the
// product response below. Supports the "this solves real problems"
// feeling immediately after the comparison section.
const RealOutcomes = () => {
  const pairs = [
    {
      pain:  "I forgot everything they told me.",
      relief: "Kindra keeps the important parts clear and easy to revisit.",
    },
    {
      pain:  "Every doctor starts from zero.",
      relief: "Kindra carries your story forward.",
    },
    {
      pain:  "My results came back and I spiralled.",
      relief: "Kindra explains what matters calmly.",
    },
    {
      pain:  "I'm managing care for someone else.",
      relief: "Kindra helps hold the moving pieces together.",
    },
  ];
  return (
    <section
      className="relative"
      style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }}
      data-testid="landing-outcomes"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="max-w-[820px] mb-14 md:mb-20">
          <p
            className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-5"
            style={{ color: TEAL, letterSpacing: '0.24em' }}
          >
            Why people need this
          </p>
          <h2
            className="font-serif text-[36px] md:text-[52px] lg:text-[60px] leading-[1.05] tracking-[-0.015em] font-normal"
            style={{ color: NAVY }}
            data-testid="outcomes-headline"
          >
            Real life doesn't happen <em className="italic">in appointments.</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {pairs.map((p, i) => (
            <article
              key={i}
              className="rounded-2xl p-7 md:p-9"
              style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
              data-testid={`outcome-card-${i}`}
            >
              <p
                className="font-serif italic text-[22px] md:text-[26px] leading-[1.3] tracking-[-0.005em] mb-5"
                style={{ color: NAVY }}
                data-testid={`outcome-card-${i}-pain`}
              >
                "{p.pain}"
              </p>
              <div
                className="pt-4 flex items-start gap-3"
                style={{ borderTop: `1px solid ${HAIRLINE}` }}
              >
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: TEAL_SOFT, color: TEAL }}
                  aria-hidden
                >
                  <CheckTick size={13} color={TEAL} />
                </span>
                <p
                  className="font-sans text-[15px] md:text-[16px] leading-[1.55]"
                  style={{ color: NAVY_MUTED }}
                  data-testid={`outcome-card-${i}-relief`}
                >
                  {p.relief}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Section 4 · What changes when someone holds the whole picture
// 6 outcome cards, cleaner than Section 2 — these are the promises,
// not the pains. Premium, quiet, confident.
const OutcomesBelt = () => {
  const items = [
    { icon: '🩺', line: 'Walk into appointments prepared.' },
    { icon: '📚', line: 'Stop re-explaining your history.' },
    { icon: '💬', line: 'Understand results without panic.' },
    { icon: '🧡', line: 'Keep family care organised.' },
    { icon: '❓', line: 'Know better next questions.' },
    { icon: '🌿', line: 'Feel less alone in the gaps.' },
  ];
  return (
    <section
      className="relative"
      style={{ backgroundColor: SAGE_BG, borderTop: `1px solid ${HAIRLINE}` }}
      data-testid="landing-outcomes-belt"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="max-w-[820px] mb-14 md:mb-20">
          <p
            className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-5"
            style={{ color: TEAL, letterSpacing: '0.24em' }}
          >
            Outcomes you'll feel
          </p>
          <h2
            className="font-serif text-[36px] md:text-[52px] lg:text-[60px] leading-[1.05] tracking-[-0.015em] font-normal"
            style={{ color: NAVY }}
            data-testid="outcomes-belt-headline"
          >
            What changes when someone holds <em className="italic">the whole picture.</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {items.map((it, i) => (
            <article
              key={i}
              className="rounded-2xl p-6 md:p-7 flex items-start gap-4"
              style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
              data-testid={`outcome-belt-${i}`}
            >
              <span
                className="inline-flex items-center justify-center w-11 h-11 rounded-full flex-shrink-0 text-[18px]"
                style={{ backgroundColor: TEAL_SOFT }}
                aria-hidden
              >
                {it.icon}
              </span>
              <p
                className="font-serif text-[18px] md:text-[20px] leading-[1.35] tracking-[-0.005em]"
                style={{ color: NAVY }}
              >
                {it.line}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── 5 · APPOINTMENT BRIEF SHOWCASE ───────────────────────
const AppointmentBrief = ({ onStart }) => (
  <section
    className="relative"
    style={{ backgroundColor: NAVY, color: PAPER }}
    data-testid="landing-brief"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-36">
      <div className="lg:grid lg:grid-cols-12 lg:gap-14 lg:items-center">
        <div className="lg:col-span-5">
          <p
            className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-5"
            style={{ color: TEAL, letterSpacing: '0.24em' }}
          >
            Appointment brief
          </p>
          <h2
            className="font-serif text-[36px] md:text-[52px] lg:text-[60px] leading-[1.05] tracking-[-0.015em] font-normal"
            data-testid="brief-headline"
          >
            Walk in already <em className="italic">understood.</em>
          </h2>
          <p
            className="font-sans mt-6 md:mt-8 text-[16px] md:text-[18px] leading-[1.55] max-w-[520px]"
            style={{ color: 'rgba(251, 250, 246, 0.78)' }}
          >
            Before every visit, Kindra builds a short, editable brief — your concerns, the context that matters, your current medications, and the questions worth asking. Print it, share it, or pull it up on your phone in the waiting room.
          </p>
          <button
            onClick={onStart}
            className="mt-9 md:mt-11 group inline-flex items-center gap-2 rounded-full px-7 md:px-8 py-4 md:py-[18px] text-[14px] md:text-[15px] font-medium font-sans transition-colors"
            style={{ backgroundColor: PAPER, color: NAVY }}
            data-testid="brief-cta"
          >
            Prepare with Kindra
            <span className="transition-transform group-hover:translate-x-0.5">
              <ArrowRight size={16} stroke={2} />
            </span>
          </button>
        </div>

        <div className="mt-12 lg:mt-0 lg:col-span-7">
          <div
            className="rounded-2xl p-6 md:p-8 max-w-[640px] mx-auto lg:mx-0 lg:ml-auto"
            style={{ backgroundColor: PAPER, color: NAVY, boxShadow: '0 40px 80px -40px rgba(0, 0, 0, 0.6)' }}
            data-testid="brief-sample"
          >
            <div className="flex items-baseline justify-between mb-5 pb-4" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
              <div>
                <p className="font-sans text-[10.5px] uppercase font-medium" style={{ color: TEAL, letterSpacing: '0.22em' }}>
                  Appointment brief
                </p>
                <p className="font-serif text-[20px] md:text-[22px] mt-1" style={{ color: NAVY }}>
                  GP — Friday, 10:30am
                </p>
              </div>
              <span
                className="text-[10px] uppercase font-medium px-2.5 py-1 rounded-full"
                style={{ backgroundColor: TEAL_SOFT, color: TEAL, letterSpacing: '0.16em' }}
              >
                Ready
              </span>
            </div>

            <div className="mb-5">
              <p className="font-sans text-[10.5px] uppercase font-medium mb-2.5" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>
                Main concerns
              </p>
              <ul className="space-y-1.5 font-sans text-[14px]" style={{ color: NAVY_MUTED }}>
                <li className="flex gap-2"><span style={{ color: TEAL }}>•</span> Fatigue worsening over 3 weeks</li>
                <li className="flex gap-2"><span style={{ color: TEAL }}>•</span> Headaches several times a week</li>
                <li className="flex gap-2"><span style={{ color: TEAL }}>•</span> Recent low ferritin (11) on March pathology</li>
              </ul>
            </div>

            <div className="mb-5">
              <p className="font-sans text-[10.5px] uppercase font-medium mb-2.5" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>
                Relevant context
              </p>
              <ul className="space-y-1.5 font-sans text-[14px]" style={{ color: NAVY_MUTED }}>
                <li className="flex gap-2"><span style={{ color: TEAL }}>—</span> Ferritin 11 µg/L on March iron studies</li>
                <li className="flex gap-2"><span style={{ color: TEAL }}>—</span> Taking Sertraline 50mg daily (8 weeks)</li>
                <li className="flex gap-2"><span style={{ color: TEAL }}>—</span> Symptoms progressing over the last 3 months</li>
              </ul>
            </div>

            <div className="mb-5">
              <p className="font-sans text-[10.5px] uppercase font-medium mb-2.5" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>
                Questions to ask
              </p>
              <ul className="space-y-1.5 font-sans text-[14px]" style={{ color: NAVY_MUTED }}>
                <li className="flex gap-2"><span style={{ color: TEAL }}>?</span> What could be contributing?</li>
                <li className="flex gap-2"><span style={{ color: TEAL }}>?</span> What should we check next?</li>
                <li className="flex gap-2"><span style={{ color: TEAL }}>?</span> What treatment options are appropriate?</li>
                <li className="flex gap-2"><span style={{ color: TEAL }}>?</span> When should I follow up?</li>
              </ul>
            </div>

            <div>
              <p className="font-sans text-[10.5px] uppercase font-medium mb-2" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>
                Current medications
              </p>
              <p className="font-sans text-[14px]" style={{ color: NAVY_MUTED }}>
                Sertraline 50mg daily
              </p>
            </div>

            <p
              className="mt-6 pt-4 font-sans italic text-[12px] md:text-[12.5px]"
              style={{ color: NAVY_SOFT, borderTop: `1px solid ${HAIRLINE}` }}
            >
              Sample brief shown — built from what you add to Kindra. A conversation aid, not a clinical document.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── 6 · HEALTH MEMORY TIMELINE ───────────────────────────
const HealthMemory = () => {
  const items = [
    { date: '14 Mar', icon: '📄', label: 'Iron studies uploaded · ferritin 11 (low)' },
    { date: '12 Mar', icon: '💬', label: 'Asked Kindra about iron results' },
    { date: '8 Mar',  icon: '💊', label: 'Started Sertraline 50mg daily' },
    { date: '1 Mar',  icon: '📝', label: 'Note: "fatigue most afternoons"' },
    { date: '24 Feb', icon: '🗓', label: 'GP visit · review in 6 weeks' },
  ];
  return (
    <section
      className="relative"
      style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }}
      data-testid="landing-memory"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="lg:grid lg:grid-cols-12 lg:gap-14 lg:items-center">
          <div className="lg:col-span-5">
            <p
              className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-5"
              style={{ color: TEAL, letterSpacing: '0.24em' }}
            >
              Health memory
            </p>
            <h2
              className="font-serif text-[36px] md:text-[48px] lg:text-[56px] leading-[1.05] tracking-[-0.015em] font-normal"
              style={{ color: NAVY }}
              data-testid="memory-headline"
            >
              Your health story, <em className="italic">remembered.</em>
            </h2>
            <p
              className="font-sans mt-6 md:mt-7 text-[16px] md:text-[17px] leading-[1.6] max-w-[500px]"
              style={{ color: NAVY_MUTED }}
            >
              Every report, note, medication and appointment quietly carries forward. So next time you ask "what changed since last time?" — the answer is already there.
            </p>
          </div>

          <div className="mt-12 lg:mt-0 lg:col-span-7">
            <div
              className="rounded-2xl p-6 md:p-8 max-w-[540px] mx-auto lg:mx-0 lg:ml-auto"
              style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}`, boxShadow: '0 12px 32px -22px rgba(14, 28, 46, 0.22)' }}
              data-testid="memory-timeline"
            >
              <p className="font-sans text-[10.5px] uppercase font-medium mb-5" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>
                Your record · last 3 weeks
              </p>
              <ol className="space-y-4">
                {items.map((it, i) => (
                  <li key={i} className="flex items-start gap-3.5" data-testid={`memory-item-${i}`}>
                    <span
                      className="mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0 text-[15px]"
                      style={{ backgroundColor: TEAL_SOFT }}
                      aria-hidden
                    >
                      {it.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-[10.5px] uppercase font-medium mb-0.5" style={{ color: NAVY_SOFT, letterSpacing: '0.16em' }}>
                        {it.date}
                      </p>
                      <p className="font-serif text-[15.5px] md:text-[16px] leading-snug" style={{ color: NAVY }}>
                        {it.label}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── 7 · TRUST STRIP ──────────────────────────────────────
const TrustStrip = () => {
  const pillars = [
    { icon: '🔒', label: 'Private by design' },
    { icon: '🤝', label: 'Never prescribes or diagnoses' },
    { icon: '🇦🇺', label: 'Built in Australia' },
    { icon: '🌿', label: 'Supports care, never replaces clinicians' },
  ];
  return (
    <section
      className="relative"
      style={{ backgroundColor: SAGE_BG, borderTop: `1px solid ${HAIRLINE}` }}
      data-testid="landing-trust"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-24">
        <div className="max-w-[760px] mx-auto text-center mb-12 md:mb-14">
          <h2
            className="font-serif text-[36px] md:text-[52px] lg:text-[60px] leading-[1.05] tracking-[-0.015em] font-normal"
            style={{ color: NAVY }}
            data-testid="trust-headline"
          >
            Built for <em className="italic">trust.</em>
          </h2>
          <p
            className="font-sans mt-5 md:mt-6 text-[16px] md:text-[18px] leading-[1.55]"
            style={{ color: NAVY_MUTED }}
            data-testid="trust-subhead"
          >
            Health information deserves extra care.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2.5 md:gap-3.5">
          {pillars.map((p, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-2.5 rounded-full pl-3 pr-5 py-2.5"
              style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
              data-testid={`trust-pillar-${i}`}
            >
              <span className="text-[15px]" aria-hidden>{p.icon}</span>
              <span className="font-sans text-[13px] md:text-[13.5px]" style={{ color: NAVY }}>
                {p.label}
              </span>
            </span>
          ))}
        </div>

        <div
          className="mt-10 md:mt-12 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 font-sans text-[12.5px]"
          style={{ color: NAVY_SOFT }}
          data-testid="trust-links"
        >
          <Link to="/privacy"            className="underline underline-offset-4 hover:text-[#0E1C2E] transition-colors" style={{ textDecorationColor: 'rgba(14,28,46,0.25)' }}>Privacy</Link>
          <span className="opacity-30" aria-hidden>·</span>
          <Link to="/security"           className="underline underline-offset-4 hover:text-[#0E1C2E] transition-colors" style={{ textDecorationColor: 'rgba(14,28,46,0.25)' }}>Security</Link>
          <span className="opacity-30" aria-hidden>·</span>
          <Link to="/terms"              className="underline underline-offset-4 hover:text-[#0E1C2E] transition-colors" style={{ textDecorationColor: 'rgba(14,28,46,0.25)' }}>Terms</Link>
          <span className="opacity-30" aria-hidden>·</span>
          <Link to="/medical-disclaimer" className="underline underline-offset-4 hover:text-[#0E1C2E] transition-colors" style={{ textDecorationColor: 'rgba(14,28,46,0.25)' }}>Medical disclaimer</Link>
        </div>
      </div>
    </section>
  );
};

// ─── 8 · TRIAL CTA ────────────────────────────────────────
const TrialCTA = ({ onStart }) => (
  <section
    className="relative"
    style={{ backgroundColor: '#FFFFFF', borderTop: `1px solid ${HAIRLINE}` }}
    data-testid="landing-trial"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 text-center">
      <h2
        className="font-serif text-[40px] md:text-[60px] lg:text-[72px] leading-[1.04] tracking-[-0.018em] font-normal max-w-[880px] mx-auto"
        style={{ color: NAVY }}
        data-testid="trial-headline"
      >
        30 days free. <em className="italic">No card needed.</em>
      </h2>
      <p
        className="font-sans mt-6 md:mt-8 text-[17px] md:text-[20px] leading-[1.5] max-w-[640px] mx-auto"
        style={{ color: NAVY_MUTED }}
        data-testid="trial-subhead"
      >
        See what healthcare feels like when someone finally remembers the whole picture.
      </p>
      <div className="mt-10 md:mt-12">
        <button
          onClick={onStart}
          className="group inline-flex items-center gap-2 rounded-full px-8 md:px-10 py-4 md:py-[20px] text-[14px] md:text-[16px] font-medium font-sans transition-colors"
          style={{ backgroundColor: NAVY, color: PAPER }}
          data-testid="trial-cta-button"
        >
          Start free
          <span className="transition-transform group-hover:translate-x-0.5">
            <ArrowRight size={16} stroke={2} />
          </span>
        </button>
      </div>
      <p
        className="mt-6 font-sans text-[12.5px]"
        style={{ color: NAVY_SOFT }}
      >
        Kindra is a health companion — it explains and remembers. It doesn't diagnose, prescribe, or replace your clinician.
      </p>
    </div>
  </section>
);

// ─── Footer ───────────────────────────────────────────────
const Footer = () => (
  <footer
    className="relative"
    style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }}
    data-testid="landing-footer"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
      <div className="flex flex-col lg:flex-row lg:items-start gap-10 lg:gap-20 lg:justify-between">
        <div className="flex-shrink-0 max-w-[320px]">
          <div className="flex items-center gap-3 mb-4">
            <img src="/kindra-logo.png" alt="Kindra" className="h-7 w-auto opacity-85" />
            <span className="font-sans text-[12px]" style={{ color: NAVY_SOFT, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              Kindra Health
            </span>
          </div>
          <p className="font-sans text-[13px] leading-[1.65]" style={{ color: NAVY_SOFT }}>
            Care between appointments. A small team based in Brisbane, Australia.
          </p>
          <p className="font-sans text-[12px] mt-4" style={{ color: NAVY_SOFT }} data-testid="footer-entity">
            © 2026 Kindra AI Pty Ltd
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 sm:gap-10 lg:gap-16">
          <div>
            <p className="font-sans text-[11px] uppercase font-medium mb-3" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>Product</p>
            <ul className="space-y-2.5 font-sans text-[13.5px]" style={{ color: NAVY_MUTED }}>
              <li><Link to="/pricing"      className="hover:text-[#0E1C2E] transition-colors" data-testid="footer-pricing">Pricing</Link></li>
              <li><Link to="/faq"          className="hover:text-[#0E1C2E] transition-colors" data-testid="footer-faq">FAQ</Link></li>
              <li><a href="/sample-brief"  target="_blank" rel="noreferrer" className="hover:text-[#0E1C2E] transition-colors" data-testid="footer-sample-brief">Sample brief</a></li>
            </ul>
          </div>
          <div>
            <p className="font-sans text-[11px] uppercase font-medium mb-3" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>Trust</p>
            <ul className="space-y-2.5 font-sans text-[13.5px]" style={{ color: NAVY_MUTED }}>
              <li><Link to="/privacy"            className="hover:text-[#0E1C2E] transition-colors" data-testid="footer-privacy">Privacy</Link></li>
              <li><Link to="/security"           className="hover:text-[#0E1C2E] transition-colors" data-testid="footer-security">Security</Link></li>
              <li><Link to="/terms"              className="hover:text-[#0E1C2E] transition-colors" data-testid="footer-terms">Terms</Link></li>
              <li><Link to="/medical-disclaimer" className="hover:text-[#0E1C2E] transition-colors" data-testid="footer-medical">Medical disclaimer</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-sans text-[11px] uppercase font-medium mb-3" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>Support</p>
            <ul className="space-y-2.5 font-sans text-[13.5px]" style={{ color: NAVY_MUTED }}>
              <li><Link to="/contact"               className="hover:text-[#0E1C2E] transition-colors" data-testid="footer-contact">Contact</Link></li>
              <li><a   href="mailto:hello@kindraai.dev" className="hover:text-[#0E1C2E] transition-colors" data-testid="footer-mail">hello@kindraai.dev</a></li>
            </ul>
          </div>
        </div>
      </div>
      <div
        className="mt-12 md:mt-16 pt-8 md:pt-10 font-sans text-[12px] md:text-[12.5px] leading-[1.7] max-w-[760px]"
        style={{ color: NAVY_SOFT, borderTop: `1px solid ${HAIRLINE}` }}
        data-testid="footer-medical-boundary"
      >
        Kindra is a health companion — it explains, organises and remembers your health information so you walk into appointments already understood. It doesn't diagnose, prescribe, or replace your clinician. In an emergency, call <strong>000</strong>.
      </div>
    </div>
  </footer>
);

// ─── Main ─────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    try {
      if (!sessionStorage.getItem('k_landing_seen')) {
        sessionStorage.setItem('k_landing_seen', '1');
        track('landing_viewed', { v: 'v13' });
      }
    } catch { /* noop */ }
  }, []);

  const [delayed, setDelayed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDelayed(true), 200);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (!delayed) return;
    if (user && user.onboarding_completed) {
      navigate('/home', { replace: true });
    }
  }, [user, delayed, navigate]);

  const goStart = () => {
    try { track(ACTIVATION_EVENTS?.CLICKED_START_FREE || 'clicked_start_free', { src: 'landing_v13' }); } catch {}
    navigate('/register');
  };
  const goLogin = () => navigate('/login');
  const goSeeExamples = () => {
    try { track('clicked_see_real_examples', { src: 'landing_v13' }); } catch {}
    if (typeof document !== 'undefined') {
      const el = document.querySelector('[data-testid="landing-outcomes"]');
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <div
      className="min-h-screen font-sans"
      style={{ backgroundColor: PAPER, color: NAVY }}
      data-testid="landing-page"
    >
      <TopNav onStart={goStart} onLogin={goLogin} user={user} />
      <Hero onStart={goStart} onSeeExamples={goSeeExamples} />
      <TrustBelt />
      <RealOutcomes />
      <WhyGenericFails />
      <OutcomesBelt />
      <AppointmentBrief onStart={goStart} />
      <HealthMemory />
      <TrustStrip />
      <TrialCTA onStart={goStart} />
      <Footer />
    </div>
  );
};

export default LandingPage;
